import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Pencil } from "lucide-react";
import { menuKeys, menuMutations, menuQueries } from "@/domain/menu/menu.query";
import type { Menu } from "@/domain/menu/menu.type";
import { MenuModal } from "./-components/layout/MenuModal";
import { PlusSquare } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/chef/menus")({
  component: RouteComponent,
  loader: ({ context: { queryClient } }) => {
    return queryClient.ensureQueryData(menuQueries.chefMenus());
  },
});

function RouteComponent() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(menuQueries.chefMenus());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState<number | null>(null);
  const updateDraftMutation = useMutation({
    ...menuMutations.updateDraft(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: menuKeys.chefMenus() });
      toast.success("Statut du menu mis à jour");
    },
  });

  const handleEdit = (menuId: number | null) => {
    setSelectedMenuId(menuId);
    setIsModalOpen(true);
  };

  const handleDraftChange = (menuId: number, draft: string) => {
    updateDraftMutation.mutate({ menuId, draft: draft === "true" });
  };

  const columns: ColumnDef<Menu>[] = [
    {
      accessorKey: "draft",
      header: "Statut",
      cell: ({ row }) => {
        const menu = row.original;
        return (
          <Select
            value={menu.draft ? "true" : "false"}
            onValueChange={(value) => handleDraftChange(menu.id, value)}
            disabled={updateDraftMutation.isPending}
          >
            <SelectTrigger
              className={`w-32 ${
                menu.draft
                  ? "bg-red-100 border-red-200 text-red-700"
                  : "bg-green-100 border-green-200 text-green-700"
              }`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="false" className="text-green-700">
                Publié
              </SelectItem>
              <SelectItem value="true" className="text-red-700">
                Brouillon
              </SelectItem>
            </SelectContent>
          </Select>
        );
      },
    },
    {
      accessorKey: "name",
      header: "Nom du menu",
    },
    {
      accessorKey: "price",
      header: "Prix",
      cell: ({ row }) => {
        const price = row.getValue("price") as number;
        return new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(price / 100);
      },
    },
    {
      id: "actions",
      header: "Modifier",
      cell: ({ row }) => {
        const menu = row.original;
        return (
          <Button size="sm" onClick={() => handleEdit(menu.id)}>
            <Pencil className="h-4 w-4" />
          </Button>
        );
      },
    },
  ];

  return (
    <section className="p-4 w-full">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="font-h1 text-dark-blue">Mes menus</h1>
          <h4 className="font-h4 text-muted-foreground">
            Gérez ici vos menus, ajoutez-en de nouveaux et modifiez ceux
            existants.
          </h4>
        </div>
      </div>

      <DataTable columns={columns} data={data ?? []} isLoading={isLoading} />

      <MenuModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        menuId={selectedMenuId}
      />

      <div className="flex justify-center mt-4">
        <Button size="sm" onClick={() => handleEdit(null)}>
          <PlusSquare className="h-4 w-4" />
          Ajouter un menu
        </Button>
      </div>
    </section>
  );
}
